/**
 * Which rows of the Accounts list a pick may name (L16 of docs/plans/ledger.md).
 *
 * "What was it for?" on Money out offers expenses, For home, a loan paid back
 * and To sort; "Paid from" offers Cash and the banks. No new field type: the
 * field is a `reference` to the Accounts list, and its choice spec
 * (`app.field.spec.accounts`) says which accounts it offers, by type and by
 * system key, and which type a category typed in the picker is made as.
 *
 * A row is offered when its type is one of `types` or its system key one of
 * `systemKeys`, and its system key is not one of `except`. Neither list set:
 * every row. `addAs` absent: the picker adds no row (drawings, loans, banks
 * are never made from a picker; the money kit seeds them).
 *
 * Pure: the server judges a pick with it, and the web and the phone filter
 * the picker with the same call.
 */

/** The five account types, spelled here so `formula.ts` can name the shape without a cycle. */
export type AccountChoiceType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export type AccountChoice = {
  readonly types?: readonly AccountChoiceType[];
  readonly systemKeys?: readonly string[];
  readonly except?: readonly string[];
  /** The type a new category typed in the picker is made as: an expense on Money out, an income on Money in. */
  readonly addAs?: 'income' | 'expense';
};

const TYPES: readonly string[] = ['asset', 'liability', 'equity', 'income', 'expense'];

/** `QUANTITY_SYSTEM_KEYS` of patterns.ts, spelled here as TYPES is: Stock, Came in, Went out, Wastage. */
const QUANTITY_KEYS: readonly string[] = ['stock', 'stock_in', 'stock_out', 'stock_loss_qty'];

const strings = (raw: unknown, max: number): string[] | null =>
  Array.isArray(raw) && raw.length <= max && raw.every((one) => typeof one === 'string' && one.length > 0 && one.length <= 40) ? (raw as string[]) : null;

/** A stored or sent account choice, read without trusting it; null when it does not read. */
export const readAccountChoice = (raw: unknown): AccountChoice | null => {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const held = raw as { types?: unknown; systemKeys?: unknown; except?: unknown; addAs?: unknown };
  const out: { types?: AccountChoiceType[]; systemKeys?: string[]; except?: string[]; addAs?: 'income' | 'expense' } = {};
  if (held.types !== undefined) {
    const types = strings(held.types, 5);
    if (types === null || types.some((one) => !TYPES.includes(one))) return null;
    out.types = types as AccountChoiceType[];
  }
  for (const member of ['systemKeys', 'except'] as const) {
    if (held[member] === undefined) continue;
    const keys = strings(held[member], 24);
    if (keys === null) return null;
    out[member] = keys;
  }
  if (held.addAs !== undefined) {
    if (held.addAs !== 'income' && held.addAs !== 'expense') return null;
    out.addAs = held.addAs;
  }
  return out;
};

/** Whether a pick limited by `choice` may name an account of this type and system key. */
export const accountAllowed = (choice: AccountChoice, account: { readonly type: string; readonly systemKey: string | null }): boolean => {
  if (account.systemKey !== null && (choice.except ?? []).includes(account.systemKey)) return false;
  // The accounts that hold quantities (Phase 9) are never money: only a pick that names one by its key offers it.
  if (account.systemKey !== null && QUANTITY_KEYS.includes(account.systemKey)) return (choice.systemKeys ?? []).includes(account.systemKey);
  const limited = choice.types !== undefined || choice.systemKeys !== undefined;
  if (!limited) return true;
  if ((choice.types ?? []).includes(account.type as AccountChoiceType)) return true;
  return account.systemKey !== null && (choice.systemKeys ?? []).includes(account.systemKey);
};

/**
 * The account a payment mode names (L45): a choice of mode stays a chip, and
 * each option is read as a row of the Accounts list by its system key. Cash
 * is Cash; UPI, a card, a cheque or a transfer is the bank; Credit, Due,
 * Unpaid, Udhaar and Room bill are not paid yet, so the customer owes it.
 * Any other mode waits in To sort (L17) until someone says where it went.
 */
export const modeSystemKey = (optionKey: string): 'cash' | 'bank' | 'receivable' | 'to_sort' => {
  const key = optionKey.trim().toLowerCase();
  if (/^(cash|nakad|naqad|rokda)/.test(key)) return 'cash';
  if (/^(upi|card|bank|cheque|check|neft|imps|rtgs|gpay|google_pay|phonepe|paytm|online|transfer|debit_card|credit_card|m_pesa|mpesa)/.test(key)) return 'bank';
  if (/^(credit|on_credit|due|unpaid|udhaar|udhar|baki|room_bill|pay_later|later|pending)$/.test(key)) return 'receivable';
  return 'to_sort';
};

/** The choices the money forms offer (L16), so the kit, the setup and the tests name one set. */
export const ACCOUNT_CHOICES = {
  /** Money in, "What was it for?": an income, a loan taken or a deposit held, owner's money put in, or To sort. */
  moneyInFor: { types: ['income', 'liability'], systemKeys: ['owner_capital', 'to_sort'], except: ['payable', 'tax_out', 'tax_out_cgst', 'tax_out_sgst', 'tax_out_utgst', 'tax_out_igst'], addAs: 'income' },
  /** Money out, "What was it for?": an expense, For home, a loan paid back or given, a deposit paid, or To sort. */
  moneyOutFor: { types: ['expense', 'liability'], systemKeys: ['drawings', 'to_sort'], except: ['payable', 'tax_out', 'tax_out_cgst', 'tax_out_sgst', 'tax_out_utgst', 'tax_out_igst', 'stock_loss'], addAs: 'expense' },
  /** Where money is: Cash and the banks, never what someone owes. */
  moneyAt: { systemKeys: ['cash', 'bank'], types: ['asset'], except: ['receivable', 'to_sort', 'tax_in', 'tax_in_cgst', 'tax_in_sgst', 'tax_in_utgst', 'tax_in_igst'] },
} as const satisfies Record<string, AccountChoice>;
